<script setup lang="ts">
    import { ref, reactive } from 'vue';
    import { TaskError, TaskErrorStatus, TaskErrorComment, OrderDirection, OrderOptions, OrderOption, TaskErrorHandler, TaskErrorCodeCount } from './HealthManager.types';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import BSFormSearch from '@shared/components/BSFormSearch.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import TaskDetailsModal from './TaskDetailsModal.vue';
    import { BackOfficeUser } from '@views/BackOffice/User/BackOfficeUser';

    const props = defineProps<{
        taskErrors: TaskError[];
        getTaskErrorStatuses: () => { name: string; value: number }[];
        orderOptions: OrderOption[];
        orderByDirection: OrderDirection;
        orderByValue: OrderOptions;
        backOfficeUsers: BackOfficeUser[] | null;
        taskErrorHandlers: TaskErrorHandler[] | null;
        loading: boolean;
        onlyMyTasks: boolean;
        errorCodeCounts: TaskErrorCodeCount[];
    }>();

    const state = reactive<{
        orderByValue: OrderOptions;
        orderByDirection: OrderDirection;
        freeTextFilter: string;
        selectedErrorCode: string;
    }>({
        orderByValue: props.orderByValue ?? OrderOptions.Created,
        orderByDirection: props.orderByDirection ?? OrderDirection.Desc,
        freeTextFilter: '',
        selectedErrorCode: '',
    });

    const emit = defineEmits<{
        (e: 'saveComments', taskErrorId: string, comments: TaskErrorComment[]): void;
        (e: 'switchAccount', accountId: string): void;
        (e: 'update:order', orderByValue: OrderOptions, orderByDirection: OrderDirection): void;
        (e: 'loadContextEntries', contextId: string): void;
        (e: 'loadBackOfficeUsers'): void;
        (e: 'assignToUser', taskError: TaskError, userId: string): void;
        (e: 'assignToMe', taskError: TaskError): void;
        (e: 'statusChange', taskError: TaskError, status: TaskErrorStatus): void;
        (e: 'handleTaskError', taskError: TaskError, handler: string): void;
        (e: 'update:filter', filter: string): void;
        (e: 'update:myTasksFilter', onlyMyTasks: boolean): void;
        (e: 'update:errorCode', errorCode: string): void;
    }>();

    const showTaskModal = ref(false);
    const selectedTaskError = ref<TaskError | null>(null);
    const selectedAccountId = ref<string | null>(null);
    const selectedTabIndex = ref(0);

    const showTaskDetails = (taskError: TaskError, tabIndex: number = 0) => {
        selectedTaskError.value = taskError;
        selectedAccountId.value = taskError.Account.AccountId;
        showTaskModal.value = true;
        selectedTabIndex.value = tabIndex;

        emit('loadBackOfficeUsers');
    };

    const closeTaskDetails = () => {
        showTaskModal.value = false;
        selectedAccountId.value = null;
        selectedTaskError.value = null;
        selectedTabIndex.value = 0;
    };

    const handleAssignToMe = (taskError: TaskError) => {
        emit('assignToMe', taskError);
    };

    const switchAccount = (accountId: string) => {
        emit('switchAccount', accountId);
    };

    const handleChangeOrder = (key: OrderOptions) => {
        if (state.orderByValue === key) {
            state.orderByDirection = state.orderByDirection === OrderDirection.Asc ? OrderDirection.Desc : OrderDirection.Asc;
        } else {
            state.orderByValue = key;
            state.orderByDirection = OrderDirection.Desc;
        }

        emit('update:order', state.orderByValue, state.orderByDirection);
    };

    const getColor = (status: TaskErrorStatus) => {
        switch (status) {
            case TaskErrorStatus.New:
                return 'var(--brand-warning)';
            case TaskErrorStatus.InProgress:
                return 'var(--brand-info)';
            case TaskErrorStatus.Solved:
                return 'var(--brand-success)';
            default:
                return '#ccc';
        }
    };

    const commentMaxLength = 50;
    const errorMaxLength = 50;

    const getCommentTooltip = (taskError: TaskError) => {
        if (!taskError.Comments?.length) return '';

        const lastComment = taskError.Comments?.[taskError.Comments.length - 1];
        const commentText = lastComment?.Text;
        const createdBy = lastComment?.CreatedBy.Name;

        return `${createdBy}: ${commentText}`;
    };

    const statusChange = (taskError: TaskError, event: Event) => {
        const target = event.target as HTMLSelectElement;
        emit('statusChange', taskError, Number(target.value) as TaskErrorStatus);
    };

    const clickedHandlers = ref<Set<string>>(new Set());

    const getMatchingHandlers = (taskError: TaskError): TaskErrorHandler[] => {
        if (!props.taskErrorHandlers || !taskError.ErrorCode) {
            return [];
        }
        return props.taskErrorHandlers.filter((handler) => handler.ErrorCode === taskError.ErrorCode);
    };

    const isHandlerClicked = (taskError: TaskError, handler: TaskErrorHandler): boolean => {
        const key = `${taskError.TaskErrorId}-${handler.TypeName}`;
        return clickedHandlers.value.has(key);
    };

    function isProcessingTaskError(taskError: TaskError): boolean {
        return taskError.Task?.TypeName === 'Job';
    }

    function navigateToProcessingTask(): void {
        const dashboardTab = document.querySelector('[href="#manageTasks"]') as HTMLElement;
        if (dashboardTab) dashboardTab.click();
        setTimeout(() => {
            window.location.hash = 'processingtasks';
        }, 100);
    }

    const handleTaskErrorClick = (taskError: TaskError, handler: string) => {
        const handlerObj = getMatchingHandlers(taskError).find((h) => h.TypeName === handler);
        if (handlerObj) {
            const key = `${taskError.TaskErrorId}-${handlerObj.TypeName}`;
            clickedHandlers.value.add(key);
        }
        emit('handleTaskError', taskError, handler);
    };

    function handleSelectErrorCode(errorCode: string) : void {
        state.selectedErrorCode = errorCode;
        emit('update:errorCode', errorCode);
    }

</script>

<template>
    <div class="d-flex flex-wrap align-items-end gap-sm">
        <div class="d-flex align-items-end gap-xxs m-auto-l">
            <BSDropdownButton v-if="props.errorCodeCounts" menuClass="dropdown-menu-right" :active="state.selectedErrorCode != ''">
                <template #toggle>Error Code</template>
                <template #content>
                    <li :class="{ active: state.selectedErrorCode === '' }">
                        <a href="javascript:void(0)" @click="handleSelectErrorCode('')">
                            All <span class="pull-right">({{ props.errorCodeCounts.map(item => item.Count).reduce((a, b) => a + b, 0) }})</span>
                        </a>
                    </li>
                    <li v-for="errorCodeCount in props.errorCodeCounts" :class="{ active: state.selectedErrorCode === errorCodeCount.ErrorCode }">
                        <a href="javascript:void(0)" @click="handleSelectErrorCode(errorCodeCount.ErrorCode)" class="d-flex gap-sm">
                            {{ errorCodeCount.ErrorCode }} <span class="m-auto-l">({{ errorCodeCount.Count }})</span>
                        </a>
                    </li>
                </template>
            </BSDropdownButton>

            <BSFormSearch
                v-model="state.freeTextFilter"
                class="search-input-width"
                inputId="account-name-search"
                :disabled="false"
                :debounce-time="400"
                :placeholder="'Filter by account name'"
                @reset="() => {
                        state.freeTextFilter = '';
                        $emit('update:filter', '');
                    }"
                @search="() => $emit('update:filter', state.freeTextFilter)"
            />

            <label class="btn" :class="props.onlyMyTasks ? 'btn-primary' : 'btn-default'" :aria-pressed="props.onlyMyTasks">
                <input
                    type="checkbox"
                    class="sr-only"
                    :checked="props.onlyMyTasks"
                    :disabled="loading"
                    @change="$emit('update:myTasksFilter', ($event.target as HTMLInputElement).checked)" />
                {{ $Resources.HealthManagerMyTasksLabel }}
            </label>

            <BSDropdownButton v-if="props.orderOptions" menuClass="dropdown-menu-right">
                <template #toggle>
                    <span class="glyphicon glyphicon-sort m-xxs-r"></span>
                    {{ $Resources.SortByBtn }}
                </template>
                <template #content>
                    <li role="presentation" class="dropdown-header">{{ $Resources.SortByLabel }}:</li>
                    <li v-for="orderOption in props.orderOptions" :class="{ active: state.orderByValue === orderOption.key }">
                        <a href="javascript:void(0)" @click="handleChangeOrder(orderOption.key)">
                            {{ orderOption.display }}
                            <span
                                class="glyphicon pull-right scale-75"
                                :class="{
                                    'glyphicon-triangle-bottom': state.orderByDirection === OrderDirection.Desc,
                                    'glyphicon-triangle-top': state.orderByDirection === OrderDirection.Asc,
                                }"
                                aria-hidden="true"
                                v-show="state.orderByValue === orderOption.key"></span>
                        </a>
                    </li>
                </template>
            </BSDropdownButton>
        </div>
    </div>

    <table class="table table-hover table-sticky-headers">
        <thead>
            <tr>
                <th class="text-nowrap">Status</th>
                <th class="text-nowrap">Created</th>
                <th class="text-nowrap">Account</th>
                <th class="text-nowrap">Details</th>
                <th class="text-nowrap">Error</th>
                <th class="text-nowrap">Comment</th>
                <th class="text-nowrap">Assigned to</th>
                <th class="text-nowrap">Action</th>
                <th class="text-right text-nowrap">Occurrences</th>
            </tr>
        </thead>
        <tbody class="td-align-middle">
            <tr v-for="taskError in taskErrors" :key="taskError.TaskErrorId">
                <td>
                    <div class="d-flex align-items-center">
                        <span class="status" :style="'background-color: ' + getColor(taskError.Status)"></span>
                        <label :for="'status-' + taskError.TaskErrorId" class="sr-only">Status:</label>
                        <select
                            :id="'status-' + taskError.TaskErrorId"
                            :value="taskError.Status"
                            @change="statusChange(taskError, $event)"
                            class="form-control"
                            style="width: 120px"
                            :disabled="loading">
                            <option v-for="status in getTaskErrorStatuses()" :key="status.value" :value="status.value">
                                {{ status.name }}
                            </option>
                        </select>
                    </div>
                </td>
                <td class="text-nowrap">
                    {{ taskError.Created ? formatDateTime(taskError.Created) : taskError.LastModified ? '' : '-' }}
                    <small class="d-block" v-if="taskError.LastModified">Modified: {{ formatDateTime(taskError.LastModified) }}</small>
                </td>
                <td>
                    <a href="javascript:void(0)" role="button" @click="switchAccount(taskError.Account.AccountId)">
                        {{ taskError.Account.Name }}
                    </a>
                </td>
                <td>
                    <a href="javascript:void(0)" role="button" @click="showTaskDetails(taskError)">
                        {{ taskError.Task?.TypeName || taskError.TaskId }}
                    </a>
                </td>
                <td>
                    <strong class="d-block">{{ taskError.ErrorCode }}</strong>
                    <span v-tooltip.top="taskError.Error?.length > errorMaxLength ? taskError.Error : ''">
                        {{ taskError.Error?.length > errorMaxLength ? taskError.Error.substring(0, errorMaxLength) + '...' : taskError.Error }}
                    </span>
                </td>
                <td>
                    <a href="javascript:void(0)" role="button" @click="showTaskDetails(taskError, 2)" v-tooltip.top="getCommentTooltip(taskError)">
                        {{
                            taskError.Comments?.length
                                ? taskError.Comments[taskError.Comments.length - 1].Text?.substring(0, commentMaxLength) +
                                  (taskError.Comments[taskError.Comments.length - 1].Text?.length > commentMaxLength
                                      ? '... (' + taskError.Comments.length + ')'
                                      : ' (' + taskError.Comments.length + ')')
                                : 'Add comment'
                        }}
                    </a>
                </td>
                <td>
                    <tempplate v-if="taskError.OwnedBy">
                        <a href="javascript:void(0)" role="button" @click="showTaskDetails(taskError)" v-tooltip.top="'Change owner'">
                            {{ taskError.OwnedBy.Name }}
                        </a>
                    </tempplate>
                    <template v-else>
                        <button class="btn btn-sm btn-default" @click="handleAssignToMe(taskError)" :disabled="loading">Assign to me</button>
                    </template>
                </td>
                <td>
                    <template v-if="taskError.Status !== TaskErrorStatus.Solved">
                        <a
                            v-if="isProcessingTaskError(taskError)"
                            href="javascript:void(0)"
                            class="btn btn-sm btn-default m-xxs-r"
                            @click="navigateToProcessingTask()"
                            title="View in Processing Tasks tab">
                            <span class="glyphicon glyphicon-link"></span> Processing Task
                        </a>
                        <button
                            v-for="(handler, index) in getMatchingHandlers(taskError)"
                            :key="`${handler.ErrorCode}-${index}`"
                            class="btn btn-sm btn-primary m-xxs-r"
                            :disabled="isHandlerClicked(taskError, handler)"
                            @click="handleTaskErrorClick(taskError, handler.TypeName)"
                            :title="handler.Description">
                            {{ handler.DisplayName }}
                        </button>
                    </template>
                </td>
                <td class="text-right">
                    <a href="javascript:void(0)" role="button" @click="$emit('loadContextEntries', taskError.TaskErrorId)">
                        {{ taskError.ErrorCount }}
                    </a>
                </td>
            </tr>
        </tbody>
    </table>

    <TaskDetailsModal
        :show="showTaskModal"
        :taskError="selectedTaskError || null"
        :accountId="selectedAccountId"
        :backOfficeUsers="props.backOfficeUsers"
        :selectedTabIndex="selectedTabIndex"
        @close="closeTaskDetails"
        @assignToUser="(taskError: TaskError, userId: string) => emit('assignToUser', taskError, userId)"
        @switchAccount="(accountId: string) => emit('switchAccount', accountId)"
        @saveComments="(taskErrorId: string, comments: TaskErrorComment[]) => emit('saveComments', taskErrorId, comments)" />
</template>

<style scoped>
    .status {
        width: 3px;
        height: 38px;
        border-radius: 1.5px;
        margin-right: 3px;
        display: block;
    }
</style>
